import { Link } from 'react-router-dom';
import Footer from '../home/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { setByPath } from '../../utils/objectPath';
import Icon from '../services/editable/commonIcon';
import EditableImage from '../services/editable/EditableImage';
import { buildMobileStyleSheet } from '../services/editable/styleUtils';
import { useEditablePage } from '../services/editable/useEditablePage';
import starIcon from '../../assets/star.svg';
import arrowIcon from '../../assets/rightarrow.svg';
import './AboutUs.css';
import './WorkingWithUsSection.css';
import './HowWeWorkSection.css';
import '../home/MeetingBannerSection.css';
import '../services/editable/editable.css';

/**
 * Generic, CMS-driven About page — the About-page counterpart to
 * TemplateServicePage. editable=false: plain public render (byte-identical
 * markup to the original hardcoded AboutUs.jsx + WorkingWithUsSection.jsx +
 * HowWeWorkSection.jsx). editable=true: full inline WYSIWYG using the same
 * shared editing helpers (useEditablePage) as the service-page template, with
 * a floating style toolbar per block and whole-section show/hide.
 *
 * The final CTA is rebuilt inline here (same classNames/markup as the shared
 * MeetingBannerSection component) rather than reusing that component, so its
 * title/subtitle/CTA text and link destination can be made editable without
 * touching MeetingBannerSection itself — it's also used, unmodified, by other
 * pages.
 */
export default function EditableAboutPage({ data, styles, editable = false, onChange, preview = false }) {
  const isEditing = editable && !preview;

  const heroRef = useScrollAnimation({ threshold: 0.2, immediate: isEditing });
  const introRef = useScrollAnimation({ threshold: 0.2, immediate: isEditing });
  const wwuRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const hwwRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const finalCtaRef = useScrollAnimation({ threshold: 0.2, immediate: isEditing });

  const {
    T,
    RemoveBtn,
    AddBtn,
    SectionToggle,
    showSection,
    isSectionHidden,
    IconPick,
    LinkPick,
  } = useEditablePage({ data, styles, onChange, isEditing });

  return (
    <div className="au-page">
      <style>{buildMobileStyleSheet(styles, 640)}</style>

      <div className="decorative-ellipse-1" />
      <div className="top-gradient-wrapper">
        <section className="au-frame" ref={heroRef} data-track-section="about-hero">
          {/* ============ Hero ============ */}
          {showSection('hero') ? (
          <div className="au-hero" id="about" data-svcpg-hidden={isEditing && isSectionHidden('hero') ? 'true' : undefined}>
            <SectionToggle sectionKey="hero" label="Hero" />
            <div className="au-hero__inner">
              <div className="au-left">
                <h1 className="au-title">
                  {T('hero.titleLine1', 'hero.titleLine1', data.hero.titleLine1, 'span')}
                  <br />
                  {T('hero.titleAccent', 'hero.titleAccent', data.hero.titleAccent, 'span', 'headline-purple')}
                </h1>

                <div className="au-paragraph">
                  {data.hero.paragraphs.map((p, i) => (
                    <p className={`au-p1${isEditing ? ' svcpg-removable' : ''}`} key={i}>
                      {T(`hero.paragraphs.${i}`, `hero.paragraphs.${i}`, p, 'span')}
                      <RemoveBtn path="hero.paragraphs" index={i} label="Remove paragraph" />
                    </p>
                  ))}
                  <AddBtn path="hero.paragraphs" label="Add paragraph" template="New paragraph." />
                </div>

                <div className="au-actions">
                  <Link className="au-btnSchedule" to={data.hero.ctaTo} aria-label="Book a consultation" data-track-cta="book-consultation">
                    {T('hero.ctaLabel', 'hero.ctaLabel', data.hero.ctaLabel, 'span', 'au-btnSchedule__text')}
                    <span className="au-btnSchedule__icon">
                      {LinkPick('hero.ctaTo', data.hero.ctaTo, <img src={arrowIcon} alt="Arrow" />, 'Hero CTA')}
                    </span>
                  </Link>
                </div>
              </div>

              <div className="au-right">
                <div className="au-ceoBox">
                  <EditableImage
                    blockId="hero.portrait"
                    editable={isEditing}
                    src={data.hero.portraitSrc}
                    alt={data.hero.portraitAlt}
                    onChange={({ src, alt }) =>
                      onChange?.({
                        data: setByPath(setByPath(data, 'hero.portraitSrc', src), 'hero.portraitAlt', alt),
                      })
                    }
                    className="au-ceoImg"
                    placeholder={
                      <div className="au-ceoImg" role="img" aria-label={data.hero.portraitAlt}>
                        <Icon name="image" size={24} />
                      </div>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          ) : null}

          {/* ============ Stats marquee ============ */}
          {showSection('stats') ? (
            <div data-svcpg-hidden={isEditing && isSectionHidden('stats') ? 'true' : undefined}>
              <SectionToggle sectionKey="stats" label="Stats marquee" />
              <div className="au-stats">
                <div className="au-stats__track">
                  {[0, 1, 2, 3].map((groupIdx) => (
                    <div className="au-stats__inner" key={groupIdx}>
                      {data.stats.items.map((item, itemIdx) => (
                        <div
                          className={`au-stats__item${isEditing && groupIdx === 0 ? ' svcpg-removable' : ''}`}
                          key={`${groupIdx}-${itemIdx}`}
                        >
                          <img src={starIcon} className="au-star" alt="" />
                          {T(`stats.items.${itemIdx}.text`, `stats.items.${itemIdx}.text`, item.text, 'span')}
                          {groupIdx === 0 ? (
                            <RemoveBtn path="stats.items" index={itemIdx} small label="Remove stat" />
                          ) : null}
                        </div>
                      ))}
                      {groupIdx === 0 ? (
                        <AddBtn path="stats.items" label="Add stat" template={{ text: 'New stat' }} />
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      {/* ============ Intro (mission / vision / approach) ============ */}
      {showSection('intro') ? (
        <section
          className="au2-section"
          ref={introRef}
          data-track-section="about-mission-vision"
          data-svcpg-hidden={isEditing && isSectionHidden('intro') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="intro" label="Mission / Vision / Approach" />
          <div className="au2-inner">
            <div className="au2-top">
              <div className="au2-pill">{T('intro.pill', 'intro.pill', data.intro.pill, 'span')}</div>
              <h2 className="au2-title">
                {T('intro.titleLine', 'intro.titleLine', data.intro.titleLine, 'span')}{' '}
                {T('intro.titleAccent', 'intro.titleAccent', data.intro.titleAccent, 'span', 'headline-purple')}
              </h2>
              <p className="au2-subtitle">{T('intro.subtitle', 'intro.subtitle', data.intro.subtitle, 'span')}</p>
            </div>

            <div className="au2-cards">
              {data.intro.cards.map((card, i) => (
                <div className={`au2-card${isEditing ? ' svcpg-removable' : ''}`} key={i}>
                  <div className="au2-card-icon">
                    <EditableImage
                      blockId={`intro.cards.${i}.image`}
                      editable={isEditing}
                      src={card.imgSrc}
                      alt={card.imgAlt}
                      onChange={({ src, alt }) =>
                        onChange?.({
                          data: setByPath(
                            setByPath(data, `intro.cards.${i}.imgSrc`, src),
                            `intro.cards.${i}.imgAlt`,
                            alt
                          ),
                        })
                      }
                      placeholder={<Icon name="image" size={20} />}
                    />
                  </div>
                  {T(`intro.cards.${i}.title`, `intro.cards.${i}.title`, card.title, 'h3', 'au2-card-title')}
                  {T(`intro.cards.${i}.desc`, `intro.cards.${i}.desc`, card.desc, 'p', 'au2-card-description')}
                  <RemoveBtn path="intro.cards" index={i} label="Remove card" />
                </div>
              ))}
            </div>
            <AddBtn
              path="intro.cards"
              label="Add card"
              template={{ imgSrc: '', imgAlt: 'New icon', title: 'New card', desc: 'Card description' }}
            />
          </div>
        </section>
      ) : null}

      {/* ============ Working with us ============ */}
      {showSection('workingWithUs') ? (
        <section
          className="wwu-section"
          ref={wwuRef}
          data-svcpg-hidden={isEditing && isSectionHidden('workingWithUs') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="workingWithUs" label="Working with us" />
          <div className="wwu-container">
            <div className="wwu-header">
              <div className="wwu-pill">
                <span className="wwu-pill-dot" aria-hidden="true" />
                <span>{T('workingWithUs.pill', 'workingWithUs.pill', data.workingWithUs.pill, 'span')}</span>
              </div>
              <h2 className="wwu-title">{T('workingWithUs.title', 'workingWithUs.title', data.workingWithUs.title, 'span')}</h2>
            </div>

            <div className="wwu-grid">
              {data.workingWithUs.cards.map((card, ci) => (
                <div key={ci} className={`wwu-card${isEditing ? ' svcpg-removable' : ''}`}>
                  <div className="wwu-card-icon-box">
                    {IconPick(`workingWithUs.cards.${ci}.icon`, `workingWithUs.cards.${ci}.icon`, card.icon, 24)}
                  </div>
                  {T(`workingWithUs.cards.${ci}.title`, `workingWithUs.cards.${ci}.title`, card.title, 'h3', 'wwu-card-title')}
                  <div className="wwu-card-divider" />
                  <ul className="wwu-card-list">
                    {card.items.map((item, ii) => (
                      <li key={ii} className={`wwu-card-item${isEditing ? ' svcpg-removable' : ''}`}>
                        <span className="wwu-check-icon">
                          <Icon name="check" size={12} />
                        </span>
                        {T(
                          `workingWithUs.cards.${ci}.items.${ii}`,
                          `workingWithUs.cards.${ci}.items.${ii}`,
                          item,
                          'span',
                          'wwu-item-text'
                        )}
                        <RemoveBtn path={`workingWithUs.cards.${ci}.items`} index={ii} small label="Remove item" />
                      </li>
                    ))}
                  </ul>
                  <AddBtn path={`workingWithUs.cards.${ci}.items`} label="Add item" template="New item" />
                  <RemoveBtn path="workingWithUs.cards" index={ci} label="Remove card" />
                </div>
              ))}
            </div>
            <AddBtn
              path="workingWithUs.cards"
              label="Add card"
              template={{ icon: 'star', title: 'New card', items: ['New item'] }}
            />
          </div>
        </section>
      ) : null}

      {/* ============ How we work ============ */}
      {showSection('howWeWork') ? (
        <section
          className="hww-section"
          ref={hwwRef}
          data-svcpg-hidden={isEditing && isSectionHidden('howWeWork') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="howWeWork" label="How we work" />
          <div className="hww-container">
            <div className="hww-top-bar">
              <div className="hww-header-left">
                <div className="hww-pill">
                  <span className="hww-pill-dot" aria-hidden="true" />
                  <span>{T('howWeWork.pill', 'howWeWork.pill', data.howWeWork.pill, 'span')}</span>
                </div>
                <h2 className="hww-title">{T('howWeWork.title', 'howWeWork.title', data.howWeWork.title, 'span')}</h2>
              </div>

              <div className="hww-header-right">
                <Link to={data.howWeWork.ctaTo} className="hww-cta-btn" aria-label={data.howWeWork.ctaLabel}>
                  {T('howWeWork.ctaLabel', 'howWeWork.ctaLabel', data.howWeWork.ctaLabel, 'span')}
                  {LinkPick('howWeWork.ctaTo', data.howWeWork.ctaTo, <Icon name="arrowRight" size={16} />, 'How we work CTA')}
                </Link>
              </div>
            </div>

            <div className="hww-grid">
              {data.howWeWork.steps.map((step, i) => (
                <div key={i} className={`hww-card${isEditing ? ' svcpg-removable' : ''}`}>
                  <div className="hww-card-header">
                    {T(`howWeWork.steps.${i}.num`, `howWeWork.steps.${i}.num`, step.num, 'span', 'hww-step-num')}
                    <div className="hww-step-line" />
                    {T(`howWeWork.steps.${i}.badge`, `howWeWork.steps.${i}.badge`, step.badge, 'span', 'hww-step-badge')}
                  </div>
                  {T(`howWeWork.steps.${i}.title`, `howWeWork.steps.${i}.title`, step.title, 'h3', 'hww-card-title')}
                  {T(`howWeWork.steps.${i}.desc`, `howWeWork.steps.${i}.desc`, step.desc, 'p', 'hww-card-desc')}
                  <RemoveBtn path="howWeWork.steps" index={i} label="Remove step" />
                </div>
              ))}
            </div>
            <AddBtn
              path="howWeWork.steps"
              label="Add step"
              template={(arr) => ({
                num: String(arr.length + 1).padStart(2, '0'),
                badge: 'New phase',
                title: 'New step',
                desc: 'Step description',
              })}
            />
          </div>
        </section>
      ) : null}

      {/* ============ Final CTA (inline rebuild of MeetingBannerSection so it's editable) ============ */}
      {showSection('finalCta') ? (
        <section
          className="meeting-banner-section au-meeting-banner-section"
          id="about-contact-cta"
          ref={finalCtaRef}
          data-track-section="about-meeting-banner"
          data-svcpg-hidden={isEditing && isSectionHidden('finalCta') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="finalCta" label="Final CTA" />
          <div className="section-container meeting-banner-section__wrap">
            <div className="meeting-banner">
              <div className="meeting-banner__decor meeting-banner__decor--bl" aria-hidden />
              <div className="meeting-banner__decor meeting-banner__decor--tr" aria-hidden />
              <div className="meeting-banner__inner">
                <h2 className="meeting-banner__title">{T('finalCta.title', 'finalCta.title', data.finalCta.title, 'span')}</h2>
                <p className="meeting-banner__sub">{T('finalCta.subtitle', 'finalCta.subtitle', data.finalCta.subtitle, 'span')}</p>
                <Link to={data.finalCta.ctaTo} className="meeting-banner__cta">
                  {T('finalCta.ctaLabel', 'finalCta.ctaLabel', data.finalCta.ctaLabel, 'span', 'meeting-banner__cta-text')}
                  <span className="meeting-banner__cta-arrow" aria-hidden>
                    {LinkPick('finalCta.ctaTo', data.finalCta.ctaTo, <img src={arrowIcon} alt="" />, 'Final CTA link')}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <Footer />
    </div>
  );
}
